import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { IUserStatus, QueuePanel, QueueInfoItem, QueueAlertChecker, QueueStats, IUser } from "../../../types";
import { selectUserStatuses } from "../statusSlice";

export interface QueueAgentStats {
    uuid: string,
    name: string,
    display_name: string,
    avatar_key: string;
    num_calls: number;
    talk_time: number;
    total_failed_times: number;
    average_ring_duration: number;
    last_call_end: string | null;
    specific_queue_login: boolean;
}

// Todo, if Importing this one there is error, need to find the solution
export const selectUsersDictionary = createSelector(
    [(state: RootState) => state.auth?.users],
    (users: IUser[]): Record<string, IUser> =>
        users ? Object.fromEntries(users.map(user => [user.uuid, user])) : {}
);

const selectQueuePanels = createSelector(
    (state: RootState) => state.queue.queueData,
    (panels: QueuePanel[]): QueuePanel[] => panels || []
);

export const selectSortedQueuePanels = createSelector(
    selectQueuePanels,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    (panels: QueuePanel[]): QueuePanel[] => panels ? panels.toSorted((a, b) => a.name.localeCompare(b.name)) : []
);

export const selectQueuePanelsDict = createSelector(
    (state: RootState) => state.queue.queueData,
    (panels: QueuePanel[]): Record<string, QueuePanel> => Object.fromEntries((panels || []).map(p => [p.uuid, p]))
);

export const selectQueuePanelByUuid = createSelector([
    selectQueuePanelsDict,
    (_: RootState, panelUuid: string) => panelUuid,
],
    (panels: Record<string, QueuePanel>, panelUuid: string): QueuePanel | undefined => panels[panelUuid]
);

export const selectQueuePanelsDictQueue = createSelector(
    (state: RootState) => state.queue.queueData,
    (panels: QueuePanel[]): Record<string, QueuePanel> => Object.fromEntries((panels || []).map(p => [p.queue_uuid, p]))
);

export const selectQueueAvailableUsers = createSelector([
    selectUserStatuses,
    selectUsersDictionary,
    (state: RootState) => state.queue.queueStats,
    selectQueuePanels,
], (statuses: Record<string, IUserStatus>, userDict, queueStats, panels): Record<string, number> => {
    if (!panels || !queueStats) {
        return {
            all: 0
        };
    }

    const unique = new Set();

    const res: Record<string, number> = {};

    panels.forEach((panel: QueuePanel) => {
        let available = 0;

        for (let i = 0; i < panel.queue_users.length; i += 1) {
            const user = userDict[panel.queue_users[i]];
            if (!user) {
                continue;
            }

            const loginUsers = queueStats[panel.queue_uuid]?.login_users;

            if (!loginUsers || !loginUsers.includes(user.uuid)) {
                continue;
            }

            const status = statuses[user.name.toLowerCase()];
            const code = status?.aux_code?.code?.toLowerCase();

            if (status && status.status === 'open' && (!code || code === 'available')) {
                unique.add(user.uuid);
                available += 1;
            }
        }

        res[panel.queue_uuid] = available;
    })

    res.all = unique.size;

    return res;
})

export const selectAvailableAgentsInQueue = createSelector([
    selectQueueAvailableUsers,
    (_: any, queue: string) => queue
],
    (statuses, queue): number => statuses[queue] || 0
);

export const selectQueuePanelUserData = createSelector([
    selectQueuePanelsDictQueue,
    selectUsersDictionary,
    (state: RootState) => state.queue.queueStats || {},
    (_: RootState, queueUuid: string[] | string | undefined) => queueUuid,
], (
    panels: Record<string, QueuePanel>,
    users: Record<string, IUser>,
    queueStats: Record<string, QueueStats>,
    queueUuid: string[] | string | undefined
): QueueAgentStats[] => {
    if (typeof queueUuid === 'string') {
        const panel = panels[queueUuid];
        const stats = queueStats[queueUuid];
        if (!panel || !stats) {
            return []
        }

        const userStats: QueueAgentStats[] = [];

        panel.queue_users.forEach(user_uuid => {
            const user = users[user_uuid];
            const stat = stats.user_stats[user_uuid];

            if (user) {
                userStats.push({
                    uuid: user.uuid,
                    name: user.name,
                    avatar_key: user.avatar_key,
                    display_name: user.nickname,
                    num_calls: stat?.num_calls ?? 0,
                    talk_time: stat?.total_call_duration ?? 0,
                    total_failed_times: stat?.total_failed_times ?? 0,
                    average_ring_duration: stat?.average_ring_duration ?? 0,
                    last_call_end: stat?.last_call_end || null,
                    specific_queue_login: stats.login_users.some(u => u === user_uuid)
                })

            }
        })

        return userStats;
    }

    const filterPanels = queueUuid && Array.isArray(queueUuid) && (queueUuid as string[]).length > 0 ? Object.fromEntries((queueUuid as string[]).map(queue => [queue, true])) : null;

    const userStats: QueueAgentStats[] = [];
    const userStatRef: Record<string, number> = {};

    Object.values(panels).forEach(panel => {
        const stats = queueStats[panel.queue_uuid];
        if (stats && (!filterPanels || filterPanels[panel.uuid])) {
            panel.queue_users.forEach(user_uuid => {
                const user = users[user_uuid];
                const stat = stats.user_stats[user_uuid];

                if (user) {
                    const statRef = userStatRef[user.uuid];
                    const userStat = !Number.isNaN(statRef) ? userStats[statRef] : undefined;

                    if (userStat) {
                        userStat.num_calls += stat?.num_calls ?? 0;
                        userStat.talk_time += stat?.total_call_duration ?? 0;
                        userStat.total_failed_times += stat?.total_failed_times ?? 0;
                        userStat.average_ring_duration += stat?.average_ring_duration ?? 0;
                    } else {
                        userStatRef[user.uuid] = userStats.length;
                        userStats.push({
                            uuid: user.uuid,
                            name: user.name,
                            avatar_key: user.avatar_key,
                            display_name: user.nickname,
                            num_calls: stat?.num_calls ?? 0,
                            talk_time: stat?.total_call_duration ?? 0,
                            total_failed_times: stat?.total_failed_times ?? 0,
                            average_ring_duration: stat?.average_ring_duration ?? 0,
                            last_call_end: null,
                            specific_queue_login: true,
                        });
                    }

                }
            })

        }
    });

    return userStats;
})


export const selectQueueAlertChecker = createSelector([
    selectQueuePanels,
    (state: RootState): Record<string, QueueStats> => state.queue.queueStats,
    selectQueueAvailableUsers,
],
    (queueData, queueStats, queueAvailableAgents): QueueAlertChecker => {
        const queueInfo: Record<string, QueueInfoItem> = {};
        let isQueueAlert = false;

        if (!queueStats) {
            return { queueInfo, isQueueAlert };
        }

        queueData?.forEach((panel) => {
            const uuid = panel.queue_uuid;

            const raw: QueueStats = queueStats[uuid] || {
                in_queue: 0,
                in_queue_longest: 0,
                stats: {},
                user_stats: {},
                login_users: [],
            };
            const thresh = panel.alerts || {};

            const agentsOnlineAlert = (raw.login_users?.length ?? 0) < (thresh.min_agents_online ?? 0);
            const callsWaitingAlert = (raw.stats.num_ringing ?? 0) > (thresh.max_call_waiting ?? 1_000_000);
            const averageWaitAlert = (raw.stats.average_wait_duration ?? 0) > (thresh.average_wait_time ?? 1_000_000);
            const longestWaitAlert = raw.in_queue_longest > (thresh.max_wait_time ?? 1_000_000);
            const availableAgentsAlert = (queueAvailableAgents[panel.queue_uuid] ?? 0) < (thresh.min_agents_available ?? 0);

            const queueHasAlert = agentsOnlineAlert || callsWaitingAlert || averageWaitAlert || longestWaitAlert || availableAgentsAlert;
            if (queueHasAlert) {
                isQueueAlert = true;
            }

            queueInfo[uuid] = {
                in_queue: raw.in_queue,
                in_queue_longest: raw.in_queue_longest,
                stats: raw.stats,
                user_stats: raw.user_stats,
                agentsOnline: raw.login_users?.length,
                alerts: {
                    agentsOnline: {
                        alert: agentsOnlineAlert,
                        threshold: thresh.min_agents_online ?? 0,
                    },
                    agentsAvailable: {
                        alert: availableAgentsAlert,
                        threshold: thresh.min_agents_available ?? 0,
                    },
                    callsWaiting: {
                        alert: callsWaitingAlert,
                        threshold: thresh.max_call_waiting ?? null,
                    },
                    averageWait: {
                        alert: averageWaitAlert,
                        threshold: thresh.average_wait_time ?? null,
                    },
                    longestWait: {
                        alert: longestWaitAlert,
                        threshold: thresh.max_wait_time ?? null,
                    },
                },
            };
        });

        return { queueInfo, isQueueAlert };
    }
);

export const selectSpecificQueueAlertChecker = createSelector([
    selectQueueAlertChecker,
    (_: RootState, queueUuid: string) => queueUuid
], ({ queueInfo }, queueUuid): QueueInfoItem | undefined => queueInfo[queueUuid]);

export const selectQueuePanelUserSipNames = createSelector([
    selectQueuePanels,
    selectUsersDictionary
],
    (panels: QueuePanel[], users: Record<string, IUser>): string[] => {
        const userNames: Set<string> = new Set();

        panels.forEach(panel => {
            panel.queue_users.forEach(userUuid => {
                const userName = users[userUuid]?.name.toLowerCase();
                if (userName) {
                    userNames.add(userName)
                }
            })
        })

        return Array.from(userNames);
    }
);
